import { legChangeText } from '@eflight/shared';
import { API, graphqlOperation } from 'aws-amplify';
import { useEffect, useState } from 'react';

import { type QueryResult } from '../../../Admin/Datasources/QueryFactory';
import {
    ApprovalStatus,
    computeOrderRollup,
    type FlightOrderLegApproval,
    type FlightOrderPendingChanges,
    FlightOrderStatus,
    isUnacknowledgedRemoval,
    type SnapshotRef,
} from '../../../Admin/FlightOrders/model/FlightOrderTypes';
import { useOnSupplementalDataSync } from '../../../utils/useOnSync';
import { getDb, type LocalDB } from '../../LocalDB/LocalDB';
import {
    flightSupplementalDataIsFlightOrder,
    FlightSupplementalDataType,
    type FlightSupplementalFlightOrder,
    type SupplementalFlightOrder,
    type SupplementalFlightOrderChange,
} from '../FlightSupplementalDataTypes';
import { itemIsUseful } from './Cleanup';

const DAY = 86400 * 1e3;
/** Same foresight as the schedule itself, so every card on screen can be answered from one query. */
const FORESIGHT_DAYS = 14;
/** ~1 order per duty day; the window can hold no more, and a page boundary would only lose legs. */
const QUERY_LIMIT = 50;

/**
 * Flight orders of one crew member for the schedule window — one query per supplemental sync,
 * addressed by the byCrewAndDay index, plus one per order that still owes an answer (see
 * loadLegApprovals).
 *
 * Leg snapshots are never fetched: they are the reservation restated, and the schedule already has
 * the reservation. What is fetched besides the rollup are the per-leg approvals, because the pilot
 * confirms a single leg from its card and the card next to it must not claim to have been answered
 * along with it. See getLegFlightOrderStates.
 */
export async function loadFlightOrders(contactId: string, db: LocalDB) {
    const from = toDayString(Date.now() - DAY);
    const to = toDayString(Date.now() + FORESIGHT_DAYS * DAY);
    const result = (await API.graphql(
        graphqlOperation(flightOrdersQuery, {
            crewResourceId: contactId,
            flightDayUtc: { between: [from, to] },
        })
    )) as QueryResult<FlightOrdersReturnType>;
    if (result.errors) {
        console.error(result.errors);

        return;
    }
    const items = result.data.listFlightOrdersByCrewAndDay.items.filter((item): item is FlightOrderItem =>
        Boolean(item)
    );
    const orders = await Promise.all(
        items
            // Only orders the pilot actually holds. A flight order is built up to three months ahead
            // and rewritten in place until OPS sends it, so an unsent one is a draft the pilot has
            // never been told about — showing it would put a document on the schedule that nobody
            // stands behind.
            .filter((item) => !item._deleted && item.status !== FlightOrderStatus.cancelled && Boolean(item.sentAt))
            .map(toSupplementalData)
            .filter(itemIsUseful)
            .map(withLegApprovals)
    );
    const table = db.flightSupplementalData;
    // The query answers for the whole window, so anything local inside it that did not come back is
    // gone (order cancelled, day regrouped, pilot taken off it) and must not linger on the cards.
    const keptIds = new Set(orders.map((order) => order.id));
    const staleIds = (await getStoredFlightOrders(db))
        .filter((stored) => stored.flightOrder.flightDay >= from && stored.flightOrder.flightDay <= to)
        .map((stored) => stored.id)
        .filter((id) => !keptIds.has(id));
    await table.bulkPut(orders);
    await table.bulkDelete(staleIds);
}

/** What the schedule card shows for one leg. Only sent orders get here — see loadFlightOrders. */
export enum LegFlightOrderState {
    /** Approved by this pilot and unchanged since. */
    approved = 'approved',
    /** Approved before, but the leg moved — the pilot has to confirm it again. */
    changed = 'changed',
    /** Handed to the pilot, not answered yet. */
    pending = 'pending',
}

export type LegFlightOrderStatus = {
    state: LegFlightOrderState;
    /**
     * What the last change notification said about THIS leg — the sentences the pilot was sent, so
     * the card can say what moved instead of only that something did. Empty when nothing outstanding
     * was notified about the leg.
     *
     * Without the leg each one names — the sync stores them shortened, because they are printed
     * under the card of that very leg, which states the date, the aircraft and the route already.
     */
    changeTexts: string[];
    /** Link to the approval screen, or null when the order carries no share token. */
    approvalLink: string | null;
    /** The order this leg belongs to — what a whole-day confirmation is addressed to. */
    orderId: string;
    /**
     * Whether the DAY still owes an answer. Not the same question as this leg's state: a leg taken
     * out of the duty day keeps the order unanswered while having no card left to answer it from,
     * which is exactly what the day's Confirm all is for.
     */
    orderNeedsConfirmation: boolean;
    /**
     * The leg is gone from the order and unacknowledged. It has no card to be answered from, so the
     * schedule lists it under its day on its own — see groupOrphanedFlightOrderLegs.
     */
    removedFromOrder: boolean;
};

/**
 * Per-leg approval state keyed by leg id (the reservation's ChildEvent id).
 *
 * Only orders that were sent are stored, so everything here is a document the pilot holds and can be
 * asked about; a draft OPS is still rewriting never reaches the schedule.
 *
 * TWO stored orders can name the same leg, and both records are right: a leg retimed into another
 * duty day joins that day's order as a leg the pilot flies, while the day it left keeps its approval
 * as an unacknowledged removal (see withLegApprovals). One entry per leg is kept, and it is always
 * the order the leg is actually flown on — a removal is a fact about the OTHER day, and letting it
 * win left a leg the pilot had confirmed on its own order asking to be confirmed from a card that
 * could not answer it.
 *
 * Only the orders of `contactId`: the device may still hold the previous crew member's — see
 * getOwnFlightOrders.
 */
export async function getLegFlightOrderStates(
    contactId: string | null,
    db: LocalDB = getDb()
): Promise<Map<string, LegFlightOrderStatus>> {
    const out = new Map<string, LegFlightOrderStatus>();
    const stored = await getOwnFlightOrders(db, contactId);
    for (const item of stored) {
        const order = item.flightOrder;
        if (order.status === FlightOrderStatus.cancelled || !order.sentAt) {
            continue;
        }
        const approvalLink = order.shareToken ? `/flight-order/${order.shareToken}` : null;
        const orderNeedsConfirmation = needsConfirmation(order);
        for (const leg of order.legs) {
            const removedFromOrder = Boolean(leg.removed);
            // Stored orders arrive in primary-key order, which says nothing about which of the two
            // records is the leg's own — so the flown one has to win from whichever side it is read.
            if (removedFromOrder && out.get(leg.legId)?.removedFromOrder === false) {
                continue;
            }
            out.set(leg.legId, {
                state: getLegState(order, leg.legId),
                changeTexts: getChanges(order, leg.legId).map((change) => change.text),
                approvalLink,
                orderId: order.id,
                orderNeedsConfirmation,
                removedFromOrder,
            });
        }
    }

    return out;
}

export function useLegFlightOrderStates(contactId: string | null): Map<string, LegFlightOrderStatus> {
    const [states, setStates] = useState<Map<string, LegFlightOrderStatus>>(new Map());
    const [reload, setReload] = useState<number>(0);
    useEffect(() => {
        let cancelled = false;
        getLegFlightOrderStates(contactId).then((newStates) => {
            if (!cancelled) {
                setStates(newStates);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [contactId, reload]);
    useOnSupplementalDataSync(() => {
        setReload(Math.random());
    });

    return states;
}

/**
 * Whether the whole order is still waiting for the pilot.
 *
 * `needsApproval` is the flag the notifier itself sweeps on — written by the lambda and by every
 * confirmation — so it is the one answer that also counts the legs that were taken out of the day
 * and never acknowledged. Those have no snapshot ref and no card; only the flag and the counters
 * know they are there. The counters are the fallback for a copy stored before the flag was read.
 */
function needsConfirmation(order: SupplementalFlightOrder): boolean {
    if (order.needsApproval !== undefined) {
        return Boolean(order.needsApproval);
    }

    return !(order.legCount > 0 && order.approvedLegCount >= order.legCount);
}

/** The last notification's sentences about one leg; empty on a copy stored before they were kept. */
function getChanges(order: SupplementalFlightOrder, legId: string): SupplementalFlightOrderChange[] {
    return (order.changes ?? []).filter((change) => change.legId === legId);
}

function getLegState(order: SupplementalFlightOrder, legId: string): LegFlightOrderState {
    // The leg's own answer, where it was read: the pilot confirms one leg at a time, so a day can
    // legitimately hold an approved leg next to one that still owes an answer.
    const status = order.legs.find((leg) => leg.legId === legId)?.status;
    if (status === ApprovalStatus.approved) {
        return LegFlightOrderState.approved;
    }
    if (status === ApprovalStatus.changed_needs_reapproval) {
        return LegFlightOrderState.changed;
    }
    if (status === ApprovalStatus.pending) {
        return LegFlightOrderState.pending;
    }

    // No approval was read for this leg — a fully approved order (which is not queried for them) or
    // a copy stored before they were. The order's own counters answer for the whole day.
    // A change outranks them: a leg can be re-versioned after the day was approved, and it is the
    // one thing the pilot has to be sent back for.
    if (getChanges(order, legId).length > 0) {
        return LegFlightOrderState.changed;
    }
    if (order.legCount > 0 && order.approvedLegCount >= order.legCount) {
        return LegFlightOrderState.approved;
    }

    return LegFlightOrderState.pending;
}

/**
 * Fills in what the pilot has answered about each leg, one query per order, and adds back the legs
 * the order has DROPPED.
 *
 * Skipped for a day that is fully approved: nothing on it can be outstanding, and its counters
 * already say so. What the query is for is the day in between — one leg confirmed from its card,
 * the next still waiting — which the rollup alone cannot express.
 *
 * A dropped leg has no snapshotRef and so no entry in `order.legs` — only its approval says it is
 * there (isUnacknowledgedRemoval). Acknowledging it stops the match, taking it off the schedule.
 */
async function withLegApprovals(item: FlightSupplementalFlightOrder): Promise<FlightSupplementalFlightOrder> {
    const order = item.flightOrder;
    if (!needsConfirmation(order)) {
        return item;
    }
    try {
        const approvals = await loadLegApprovals(order.id);
        const byLeg = new Map(approvals.map((approval) => [approval.legId, approval.status]));
        const normalLegs = order.legs.map((leg) => ({ ...leg, status: byLeg.get(leg.legId) }));
        const covered = new Set(order.legs.map((leg) => leg.legId));
        const removedLegs = approvals
            .filter((approval) => isUnacknowledgedRemoval(approval) && !covered.has(approval.legId))
            .map((approval) => ({
                legId: approval.legId,
                role: approval.role,
                status: approval.status,
                removed: true,
            }));

        return {
            ...item,
            flightOrder: {
                ...order,
                legs: [...normalLegs, ...removedLegs],
            },
        };
    } catch (error) {
        // Losing the per-leg answers costs precision on one card, not the schedule: getLegState
        // falls back to the order's counters.
        console.error(`FlightOrder ${order.id}: could not load leg approvals`, error);

        return item;
    }
}

async function getStoredFlightOrders(db: LocalDB): Promise<FlightSupplementalFlightOrder[]> {
    const stored = await db.flightSupplementalData.where({ type: FlightSupplementalDataType.flightOrder }).toArray();

    return stored.filter(flightSupplementalDataIsFlightOrder);
}

/**
 * The stored orders that are this crew member's own.
 *
 * A TLB is shared by whoever flies the aircraft, and the orders of the previous crew member stay on
 * it until the next supplemental sync replaces them. Read without this, a leg handed over from one
 * pilot to the other showed the previous pilot's answer on the new pilot's card, and its Confirm went
 * to the previous pilot's order — where it can only fail. A copy stored before the owner was kept is
 * left out too: it is re-read with the owner on the next sync.
 */
async function getOwnFlightOrders(db: LocalDB, contactId: string | null): Promise<FlightSupplementalFlightOrder[]> {
    if (!contactId) {
        return [];
    }

    return (await getStoredFlightOrders(db)).filter((item) => item.flightOrder.crewResourceId === contactId);
}

function toSupplementalData(item: FlightOrderItem): FlightSupplementalFlightOrder {
    return {
        id: item.id,
        // A duty day can end after midnight local time; a day of slack keeps the card readable for
        // the whole of it and still lets cleanupSupplementalData drop the order shortly after.
        usefulnessEndsByDate: new Date(new Date(`${item.flightDay}T00:00:00Z`).getTime() + 2 * DAY),
        type: FlightSupplementalDataType.flightOrder,
        flightOrder: {
            id: item.id,
            crewResourceId: item.crewResourceId,
            flightDay: item.flightDay,
            shareToken: item.shareToken ?? null,
            status: item.status,
            sentAt: item.sentAt ?? null,
            needsApproval: item.needsApproval ?? null,
            legCount: item.legCount ?? 0,
            approvedLegCount: item.approvedLegCount ?? 0,
            legs: parseSnapshotRefs(item.snapshotRefs).map((ref) => ({ legId: ref.legId, role: ref.role })),
            // Only the leg and the sentence: the rest of a change (kind, changed field keys) drives
            // the PDF highlighting, which the schedule does not render.
            changes: (parsePendingChanges(item.pendingChanges)?.changes ?? []).map((change) => ({
                legId: change.legId,
                text: change.text,
            })),
        },
    };
}

function parseSnapshotRefs(raw: string | null | undefined): SnapshotRef[] {
    if (!raw) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw) as SnapshotRef[];

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        console.error('FlightOrder: could not parse snapshotRefs', raw);

        return [];
    }
}

function parsePendingChanges(raw: string | null | undefined): FlightOrderPendingChanges | undefined {
    if (!raw) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(raw) as FlightOrderPendingChanges;
        if (!Array.isArray(parsed?.changes)) {
            return undefined;
        }

        // Sentences the lambda wrote before the leg was dropped from them still carry it; taking it
        // out here means only one shape of change ever reaches a card. See legChangeText.
        return { ...parsed, changes: parsed.changes.map((change) => ({ ...change, text: legChangeText(change) })) };
    } catch {
        console.error('FlightOrder: could not parse pendingChanges', raw);

        return undefined;
    }
}

function toDayString(time: number): string {
    return new Date(time).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Confirming a leg from its schedule card
// ---------------------------------------------------------------------------

/** GraphQL errors arrive untyped from the API; this is what the card gets to show. */
function graphqlError(errors: unknown): Error {
    const messages = Array.isArray(errors)
        ? errors.map((error: { message?: string }) => error.message).filter(Boolean)
        : [];

    return new Error(messages.length > 0 ? messages.join(', ') : 'The flight order could not be saved.');
}

/** The pilot's answers on one order, newest state from the server. */
async function loadLegApprovals(flightOrderId: string): Promise<FlightOrderLegApproval[]> {
    const result = (await API.graphql(
        graphqlOperation(legApprovalsQuery, { flightOrderId })
    )) as QueryResult<LegApprovalsReturnType>;
    if (result.errors) {
        throw graphqlError(result.errors);
    }

    return (result.data.listFlightOrderApprovalsByFlightOrder.items ?? []).filter(
        (item): item is FlightOrderLegApproval & { _deleted?: boolean | null } => Boolean(item) && !item?._deleted
    );
}

/**
 * Confirms ONE leg of a flight order — what the CONFIRM on a schedule card does, without leaving the
 * schedule for the approval page. The pilot answers the flight they are looking at; the rest of the
 * day keeps waiting for its own cards.
 */
export async function confirmFlightOrderLeg(
    legId: string,
    crewResourceId: string,
    approvedBy: string,
    db: LocalDB = getDb()
): Promise<void> {
    const holders = (await getOwnFlightOrders(db, crewResourceId)).filter((item) =>
        item.flightOrder.legs.some((leg) => leg.legId === legId)
    );
    // The order the leg is FLOWN on answers for it. Another order may name the same leg as a
    // removal — the day it was retimed out of — and confirming against that one would settle
    // nothing here: every approval on it is already answered or is about the leg being gone.
    const stored =
        holders.find((item) => item.flightOrder.legs.some((leg) => leg.legId === legId && !leg.removed)) ?? holders[0];
    if (!stored) {
        throw new Error('This flight is not part of a flight order.');
    }

    return confirmApprovals(stored.flightOrder.id, crewResourceId, approvedBy, (approval) => approval.legId === legId);
}

/**
 * Confirms the WHOLE duty day — the schedule's Confirm all, and the only way to settle a leg that was
 * taken out of the day: such a leg has no card to be answered from, but its approval keeps the order
 * unanswered and the reminders coming until somebody acknowledges it.
 */
export async function confirmFlightOrderDay(
    orderId: string,
    crewResourceId: string,
    approvedBy: string
): Promise<void> {
    return confirmApprovals(orderId, crewResourceId, approvedBy, () => true);
}

/**
 * The write behind both, and the same one the approval page makes: each answered approval is pinned
 * to the version and hash the pilot has just seen, then the order's rollup is recomputed from every
 * approval and written back — which is what OPS reads and what takes the day out of the notification
 * sweep once nothing is owed on it.
 *
 * Everything is re-read from the server first: the local copy carries no record versions, and a
 * stale one would be rejected by AppSync anyway. That also means this needs a connection — offline
 * there is nothing to queue it into, and the caller surfaces the failure.
 */
async function confirmApprovals(
    orderId: string,
    crewResourceId: string,
    approvedBy: string,
    wanted: (approval: FlightOrderLegApproval) => boolean
): Promise<void> {
    const order = await loadFlightOrderForConfirm(orderId);
    if (!order) {
        throw new Error('This flight order is no longer available.');
    }
    // The approval is the pilot's own statement, so it is only ever theirs to make. The API enforces
    // nothing of the sort — this is what keeps a shared device from answering for somebody else.
    if (order.crewResourceId !== crewResourceId) {
        throw new Error('Only the crew member this flight order belongs to can confirm it.');
    }

    const approvals = await loadLegApprovals(order.id);
    // One answer for both kinds of item: `approved` means "I have seen and accept this". Whether the
    // pilot accepted a leg they will fly or a leg that was taken away stays on legState, which is
    // never touched here, so the two facts remain readable side by side afterwards.
    const toConfirm = approvals.filter(
        (approval) =>
            wanted(approval) &&
            approval.status !== ApprovalStatus.approved &&
            approval.status !== ApprovalStatus.cancelled
    );
    if (toConfirm.length === 0) {
        return;
    }

    const now = new Date().toISOString();
    for (const approval of toConfirm) {
        await putItem(updateApprovalMutation, {
            id: approval.id,
            _version: approval._version,
            status: ApprovalStatus.approved,
            approvedVersion: approval.latestVersion,
            approvedContentHash: approval.latestContentHash ?? null,
            approvedAt: now,
            approvedBy,
            // The pilot has just answered on the current version themselves, so there is no longer a
            // reverted change of theirs left to explain — see revertedAt in schema.graphql.
            revertedAt: null,
        });
    }

    const confirmed = new Set(toConfirm.map((approval) => approval.id));
    const nextApprovals = approvals.map((approval) =>
        confirmed.has(approval.id) ? { ...approval, status: ApprovalStatus.approved } : approval
    );
    const rollup = computeOrderRollup(order, nextApprovals);
    // A fully confirmed order has nothing outstanding left to explain: the pilot has just accepted
    // exactly the diff that was highlighted, so the next render of the document comes out clean.
    const settled = rollup.status === FlightOrderStatus.approved;
    await putItem(updateOrderMutation, {
        id: order.id,
        _version: order._version,
        ...rollup,
        ...(settled ? { pendingChanges: null } : {}),
    });
}

/** The order as the rollup needs it: parsed refs, and the version the write is made against. */
async function loadFlightOrderForConfirm(id: string): Promise<ConfirmableOrder | undefined> {
    const result = (await API.graphql(graphqlOperation(flightOrderByIdQuery, { id }))) as QueryResult<{
        getFlightOrder: (FlightOrderItem & { crewResourceId: string; _version?: number | null }) | null;
    }>;
    if (result.errors) {
        throw graphqlError(result.errors);
    }
    const item = result.data.getFlightOrder;
    if (!item || item._deleted) {
        return undefined;
    }

    return {
        id: item.id,
        crewResourceId: item.crewResourceId,
        status: item.status,
        sentAt: item.sentAt ?? null,
        snapshotRefs: parseSnapshotRefs(item.snapshotRefs),
        _version: item._version,
    };
}

type ConfirmableOrder = {
    id: string;
    crewResourceId: string;
    status: string;
    sentAt: string | null;
    snapshotRefs: SnapshotRef[];
    _version?: number | null;
};

async function putItem(mutation: string, input: Record<string, unknown>): Promise<void> {
    const result = (await API.graphql(graphqlOperation(mutation, { input }))) as QueryResult<unknown>;
    if (result.errors) {
        throw graphqlError(result.errors);
    }
}

const legApprovalsQuery = `
query flightOrderLegApprovals($flightOrderId: ID!) {
    listFlightOrderApprovalsByFlightOrder(flightOrderId: $flightOrderId, limit: ${QUERY_LIMIT}) {
        items {
            id
            legId
            role
            status
            legState
            latestVersion
            latestContentHash
            _version
            _deleted
        }
    }
}`;

const flightOrderByIdQuery = `
query flightOrderForConfirm($id: ID!) {
    getFlightOrder(id: $id) {
        id
        crewResourceId
        status
        sentAt
        snapshotRefs
        _version
        _deleted
    }
}`;

const updateApprovalMutation = `
mutation confirmFlightOrderLeg($input: UpdateFlightOrderLegApprovalInput!) {
    updateFlightOrderLegApproval(input: $input) {
        id
        status
        _version
    }
}`;

const updateOrderMutation = `
mutation updateFlightOrderRollup($input: UpdateFlightOrderInput!) {
    updateFlightOrder(input: $input) {
        id
        status
        approvedLegCount
        needsApproval
        _version
    }
}`;

type LegApprovalsReturnType = {
    listFlightOrderApprovalsByFlightOrder: {
        items: Array<(FlightOrderLegApproval & { _deleted?: boolean | null }) | null>;
    };
};

/**
 * Filtered on the deprecated `flightDayUtc` twin, which is what still keys the byCrewAndDay index,
 * while `flightDay` is what carries the value — see Admin/FlightOrders/Datasources/queries.ts.
 */
const flightOrdersQuery = `
query updateSupplementalFlightOrders($crewResourceId: ID!, $flightDayUtc: ModelStringKeyConditionInput) {
    listFlightOrdersByCrewAndDay(crewResourceId: $crewResourceId, flightDayUtc: $flightDayUtc, limit: ${QUERY_LIMIT}) {
        items {
            id
            crewResourceId
            flightDay
            shareToken
            status
            sentAt
            needsApproval
            legCount
            approvedLegCount
            snapshotRefs
            pendingChanges
            _deleted
        }
    }
}`;

type FlightOrderItem = {
    id: string;
    crewResourceId: string;
    flightDay: string;
    shareToken?: string | null;
    status: string;
    sentAt?: string | null;
    needsApproval?: string | null;
    legCount?: number | null;
    approvedLegCount?: number | null;
    snapshotRefs?: string | null;
    pendingChanges?: string | null;
    _deleted?: boolean | null;
};

type FlightOrdersReturnType = {
    listFlightOrdersByCrewAndDay: { items: Array<FlightOrderItem | null> };
};
